import React, { useState, useEffect } from 'react';
import { useTheme } from '../../features/theme/context/ThemeContext';
import nvgtrLogo_Black from '../../assets/nvgtrLogo_Black.svg';
import nvgtrLogo_White from '../../assets/nvgtrLogo_White.svg';
import Lottie from 'lottie-react';
import maintenanceFallback from '../../assets/maintenance_fallback.svg';
import maintenanceFallbackLight from '../../assets/maintenance_fallback_light.svg';

const SystemStatusScreen = ({ status, onRetry }) => {
  const { darkMode } = useTheme();
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [animationData, setAnimationData] = useState(null);

  // Load animation data based on theme
  useEffect(() => {
    const loadAnimation = async () => {
      try {
        const animationFile = darkMode ? 'maintenance_light.json' : 'maintenance.json';
        const response = await fetch(`/${animationFile}`);
        if (response.ok) {
          const data = await response.json();
          setAnimationData(data);
          console.log('Animation loaded successfully:', animationFile);
        } else {
          console.error('Failed to load animation:', response.status);
        }
      } catch (error) {
        console.error('Error loading animation:', error);
      }
    };

    loadAnimation();
  }, [darkMode]);

  // Calculate actual elapsed time since status was activated
  useEffect(() => {
    const calculateElapsedTime = () => {
      if (status?.activatedAt) {
        const activatedTime = new Date(status.activatedAt);
        const now = new Date();
        const diffInMinutes = Math.floor((now - activatedTime) / (1000 * 60));
        setTimeElapsed(Math.max(0, diffInMinutes));
      } else {
        setTimeElapsed(0);
      }
    };

    // Calculate immediately
    calculateElapsedTime();

    // Update every minute
    const interval = setInterval(calculateElapsedTime, 60000);

    return () => clearInterval(interval);
  }, [status?.activatedAt]);

  // Format time elapsed into hours and minutes
  const formatTimeElapsed = (minutes) => {
    if (minutes < 1) return '<1 minute';
    if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (remainingMinutes === 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''}`;
    } else {
      return `${hours} hour${hours !== 1 ? 's' : ''} ${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}`;
    }
  };

  // Get maintenance animation based on theme
  const getMaintenanceAnimation = () => {
    // Fallback SVG if Lottie fails or is loading - use theme-appropriate static version
    const fallbackIcon = (
      <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center">
        <img 
          src={darkMode ? maintenanceFallbackLight : maintenanceFallback}
          alt="Maintenance" 
          className="w-full h-full"
        />
      </div>
    );

    // Show fallback while loading or if no animation data
    if (!animationData) {
      return fallbackIcon;
    }

    try {
      return (
        <div className="w-20 h-20 mx-auto mb-6">
          <Lottie
            animationData={animationData}
            loop={true}
            autoplay={true}
            style={{ width: '100%', height: '100%' }}
            onError={(error) => {
              console.error('Lottie error:', error);
            }}
          />
        </div>
      );
    } catch (error) {
      console.error('Error rendering Lottie animation:', error);
      return fallbackIcon;
    }
  };

  // Format estimated duration
  const formatDuration = (duration) => {
    if (!duration || duration === 'unknown') return null;
    return duration;
  };

  // Format scheduled times
  const formatScheduledTime = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleString();
  };



  const accentColor = status.statusPageStyle?.accentColor || '#3b82f6';

  return (
    <div className={`flex items-center justify-center min-h-screen bg-white dark:bg-[#030303] relative pb-[env(safe-area-inset-bottom)] overflow-x-hidden`}>
      {/* Gradient overlay - same as onboarding */}
      <div 
        className="absolute left-0 right-0 bottom-0 pointer-events-none"
        style={{
          height: '40vh',
          background: darkMode 
            ? 'radial-gradient(ellipse 120% 100% at center bottom, rgba(255, 189, 163, 0.15) 0%, rgba(189, 204, 255, 0.08) 50%, rgba(189, 204, 255, 0.04) 70%, rgba(189, 204, 255, 0.02) 85%, rgba(0, 0, 0, 0) 100%)'
            : 'radial-gradient(ellipse 120% 100% at center bottom, rgba(255, 189, 163, 0.6) 0%, rgba(189, 204, 255, 0.4) 50%, rgba(189, 204, 255, 0.25) 70%, rgba(189, 204, 255, 0.15) 85%, rgba(255, 255, 255, 0) 100%)'
        }}
      />
      
      {/* Logo - same positioning as onboarding */}
      <img 
        src={darkMode ? nvgtrLogo_White : nvgtrLogo_Black}
        alt="Midus" 
        className="absolute top-[105px] md:top-[55px] left-1/2 transform -translate-x-1/2 h-8 px-4 md:px-0"
      />
      
      {/* Main content container - same max-width as onboarding */}
      <div className="w-full p-8 mt-[8px] md:mt-[0px] max-w-[590px]">
        <div className="text-center">
          
          {/* Status Animation */}
          {getMaintenanceAnimation()}
          


          {/* Title */}
          <h1 className={`text-3xl md:text-4xl font-bold mb-6 ${darkMode ? 'text-white' : 'text-[#181818]'}`}>
            {status.title}
          </h1>

          {/* Message */}
          <p className={`text-lg md:text-xl mb-8 leading-relaxed ${darkMode ? 'text-[#787878]' : 'text-[#666666]'}`}>
            {status.message}
          </p>

          {/* Progress Bar */}
          {status.showProgressBar && (
            <div className="mb-8">
              <div className={`w-full ${darkMode ? 'bg-[#1a1a1a]' : 'bg-gray-200'} rounded-full h-2`}>
                <div 
                  className="h-2 rounded-full transition-all duration-300"
                  style={{ 
                    backgroundColor: '#ffffff',
                    width: `${status.progressPercentage || 0}%`
                  }}
                />
              </div>
              <p className={`text-sm mt-3 ${darkMode ? 'text-[#787878]' : 'text-[#666666]'}`}>
                Progress: {status.progressPercentage || 0}%
              </p>
            </div>
          )}

          {/* Time Information */}
          {(formatDuration(status.estimatedDuration) || formatScheduledTime(status.scheduledStart) || formatScheduledTime(status.scheduledEnd) || timeElapsed >= 0) && (
            <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 p-6 rounded-2xl border ${darkMode ? 'bg-[#111111] border-[#1a1a1a]' : 'bg-white border-gray-200'} shadow-sm`}>
              {formatDuration(status.estimatedDuration) && (
                <div className="text-center">
                  <h3 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-[#787878]' : 'text-[#666666]'}`}>
                    Estimated Duration
                  </h3>
                  <p className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-[#181818]'}`}>
                    {formatDuration(status.estimatedDuration)}
                  </p>
                </div>
              )}
              
              {formatScheduledTime(status.scheduledStart) && (
                <div className="text-center">
                  <h3 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-[#787878]' : 'text-[#666666]'}`}>
                    Started At
                  </h3>
                  <p className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-[#181818]'}`}>
                    {formatScheduledTime(status.scheduledStart)}
                  </p>
                </div>
              )}
              
              {formatScheduledTime(status.scheduledEnd) && (
                <div className="text-center">
                  <h3 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-[#787878]' : 'text-[#666666]'}`}>
                    Expected End
                  </h3>
                  <p className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-[#181818]'}`}>
                    {formatScheduledTime(status.scheduledEnd)}
                  </p>
                </div>
              )}

              <div className="text-center">
                <h3 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-[#787878]' : 'text-[#666666]'}`}>
                  Time Elapsed
                </h3>
                <p className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-[#181818]'}`}>
                  {formatTimeElapsed(timeElapsed)}
                </p>
              </div>
            </div>
          )}

          {/* Affected Services */}
          {status.affectedServices && status.affectedServices.length > 0 && !status.affectedServices.includes('all') && (
            <div className="mb-8">
              <h3 className={`text-sm font-semibold mb-4 ${darkMode ? 'text-[#787878]' : 'text-[#666666]'}`}>
                Affected Services
              </h3>
              <div className="flex flex-wrap justify-center gap-3">
                {status.affectedServices.map((service, index) => (
                  <span 
                    key={index}
                    className={`px-4 py-2 rounded-full text-sm font-medium ${darkMode ? 'bg-[#1a1a1a] text-[#787878] border border-[#2a2a2a]' : 'bg-gray-100 text-[#666666] border border-gray-200'}`}
                  >
                    {service.charAt(0).toUpperCase() + service.slice(1)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            {onRetry && (
              <button
                onClick={onRetry}
                className={`px-8 py-3 rounded-xl font-medium transition-all duration-200 border ${
                  darkMode 
                    ? 'bg-[#1a1a1a] hover:bg-[#2a2a2a] text-white border-[#2a2a2a] hover:border-[#3a3a3a]' 
                    : 'bg-white hover:bg-gray-50 text-[#181818] border-gray-200 hover:border-gray-300'
                } shadow-sm hover:shadow-md`}
              >
                Check Again
              </button>
            )}
            
            {status.contactInfo?.statusPageUrl && (
              <a
                href={status.contactInfo.statusPageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-3 rounded-xl font-medium transition-all duration-200 text-white shadow-sm hover:shadow-md transform hover:scale-105"
                style={{ backgroundColor: accentColor }}
              >
                Status Page
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Footer - same as onboarding */}
      <div className="absolute bottom-[max(1rem,env(safe-area-inset-bottom))] left-1/2 transform -translate-x-1/2 opacity-30 text-[#181818] dark:text-[#787878] text-[13px] font-normal text-center whitespace-nowrap px-4 md:px-0 w-full md:w-auto">
        <div className="flex flex-col md:flex-row items-center justify-center space-y-1 md:space-y-0 md:space-x-1">
          <div className="flex items-center">
            <span className="md:hidden">© {new Date().getFullYear()} Midus, a subsidiary of AgentCore LLC. All Rights Reserved.</span>
            <span className="hidden md:inline">© {new Date().getFullYear()} Midus, a subsidiary of AgentCore LLC. All Rights Reserved.</span>
          </div>
          {status.contactInfo?.showSupport && (
            <div className="flex items-center space-x-1">
              <span className="hidden md:inline md:mx-1">•</span>
              <a 
                href={`mailto:${status.contactInfo.supportEmail || 'support@heymidus.com'}`}
                className="hover:opacity-100 transition-opacity duration-200 underline"
                style={{ color: accentColor }}
              >
                Contact Support
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemStatusScreen;
